// Cotización (COV) · docs/108 B7. Usa la MISMA grilla y el MISMO cálculo que la Nota de Venta
// (previsualizar en el backend: precio, escala, impuestos y totales nunca se estiman aquí). Ciclo:
// borrador → emitida (se entrega al cliente) → convertida en NV (elige bodega) o anulada/descartada.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { api } from '../api/cliente';
import { useDisponibilidad, usePrevisualizacion } from '../api/consultas';
import type { LineaNota, Totales } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { Grilla, type Columna } from '../comun/Grilla';
import { decimal, fecha, pesos } from '../comun/formato';
import { DialogoMotivo } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from './BuscadorCliente';
import { BuscadorProducto } from './BuscadorProducto';
import {
  borradorNuevo,
  clienteDesdeBusqueda,
  cuerpoCrear,
  cuerpoPrevisualizar,
  reducirBorrador,
  type Borrador,
} from './borrador';
import { resolverCodigo } from './codigo';
import { GrillaLineas, type GrillaLineasRef } from './GrillaLineas';

export type EstadoCotizacion = 'BORRADOR' | 'EMITIDA' | 'CONVERTIDA' | 'ANULADA' | 'DESCARTADA';
export const NOMBRE_ESTADO_COT: Record<EstadoCotizacion, string> = {
  BORRADOR: 'Borrador',
  EMITIDA: 'Emitida',
  CONVERTIDA: 'Convertida en NV',
  ANULADA: 'Anulada',
  DESCARTADA: 'Descartada',
};

interface CotizacionLeida {
  cotizacionId: string;
  numero: string;
  numeroVisible: string;
  estado: EstadoCotizacion;
  version: number;
  empresaId: string;
  validaHasta: string;
  vigente: boolean;
  notaVentaId: string | null;
  contenido: {
    cabecera: {
      canal: string;
      clienteEntidadId: string;
      clienteRut: string | null;
      clienteRazonSocial: string;
      listaPrecioId: string;
      vendedorId: string | null;
      documentoSolicitado: string;
      fechaNegocio: string;
      validaHasta: string;
      observaciones: string | null;
    };
    lineas: LineaNota[];
    cargos: Array<{ tipo: string; descripcion: string; bruto: string }>;
    totales: Totales;
  };
}

export interface CotizacionListada {
  id: string;
  numero: string;
  numeroVisible: string;
  estado: EstadoCotizacion;
  cliente_entidad_id: string;
  cliente_razon_social: string;
  fecha_negocio: string;
  valida_hasta: string;
  version: number;
}

const claveCot = (e: string, id: string) => ['emp', e, 'cotizaciones', id] as const;

function useAccionCotizacion(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, { ruta: string; cuerpo: unknown; metodo?: 'POST' | 'PATCH' }>({
    mutationFn: ({ ruta, cuerpo, metodo = 'POST' }) => {
      const idempotencia = intentos.clave(`cot:${ruta}`, cuerpo);
      return metodo === 'PATCH'
        ? api.patch(ruta, cuerpo, { idempotencia })
        : api.post(ruta, cuerpo, { idempotencia });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'cotizaciones'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
}

const enDias = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' });
};

export function Cotizacion({
  cotizacionId,
  alCreada,
  alAbrirNota,
}: {
  cotizacionId?: string | undefined;
  alCreada: (id: string, numero: string) => void;
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: claveCot(empresaId, cotizacionId ?? ''),
    queryFn: () => api.get<CotizacionLeida>(`/ventas/cotizaciones/${cotizacionId}`),
    enabled: !!cotizacionId,
  });
  const [editando, setEditando] = useState(false);
  if (!cotizacionId) return <EditorCotizacion alGuardada={alCreada} />;
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="pantalla apagado">Cargando cotización…</p>;
  if (editando)
    return (
      <EditorCotizacion
        cot={q.data}
        alGuardada={() => setEditando(false)}
        alCancelar={() => setEditando(false)}
      />
    );
  return (
    <FichaCotizacion c={q.data} alEditar={() => setEditando(true)} alAbrirNota={alAbrirNota} />
  );
}

// ================================================================ editor

function EditorCotizacion({
  cot,
  alGuardada,
  alCancelar,
}: {
  cot?: CotizacionLeida;
  alGuardada: (id: string, numero: string) => void;
  alCancelar?: () => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCotizacion(empresaId);
  const grilla = useRef<GrillaLineasRef>(null);
  const inicial = useMemo<Borrador>(() => {
    const b = borradorNuevo('VENDEDOR', '');
    if (!cot) return b;
    const c = cot.contenido.cabecera;
    return {
      ...b,
      cliente: {
        id: c.clienteEntidadId,
        rut: c.clienteRut,
        razonSocial: c.clienteRazonSocial,
        condicionPago: null,
        bloqueado: false,
        estado: 'activa',
      },
      listaPrecioId: c.listaPrecioId,
      vendedorId: c.vendedorId ?? '',
      observaciones: c.observaciones ?? '',
      lineas: cot.contenido.lineas.map((l, i) => ({
        clave: `c${i + 1}`,
        productoId: l.productoId,
        sku: l.sku,
        nombre: l.descripcion,
        presentacionId: l.presentacionId,
        cantidad: decimal(l.cantidad).replace(/\./g, ''),
        precioManual: null,
        descuento:
          l.descuentoTipo && l.descuentoValor
            ? { tipo: l.descuentoTipo, valor: decimal(l.descuentoValor).replace(/\./g, '') }
            : null,
      })),
      secuencia: cot.contenido.lineas.length + 1,
    };
  }, [cot]);
  const [b, despachar] = useReducer(reducirBorrador, inicial);
  const [validaHasta, setValidaHasta] = useState(cot?.validaHasta.slice(0, 10) ?? enDias(15));
  const [texto, setTexto] = useState('');
  const [dialogo, setDialogo] = useState<null | 'cliente' | { producto: string }>(null);
  const [enfocar, setEnfocar] = useState<string | null>(null);
  const bodega = b.ubicacionId || bodegas.lista[0]?.id || '';
  useEffect(() => {
    if (!b.ubicacionId && bodega) despachar({ tipo: 'cabecera', campos: { ubicacionId: bodega } });
  }, [b.ubicacionId, bodega]);
  useEffect(() => {
    if (enfocar) {
      grilla.current?.enfocarCantidad(enfocar);
      setEnfocar(null);
    }
  }, [enfocar, b.lineas]);
  const stock = useDisponibilidad(empresaId, { ubicacionId: bodega || null });
  const cuerpoPrev = useMemo(() => cuerpoPrevisualizar(b, empresaId), [b, empresaId]);
  const prev = usePrevisualizacion(empresaId, cuerpoPrev);
  const vista = prev.datos;

  const agregar = (
    producto: { id: string; sku: string; nombre: string },
    base: { id: string },
    equivalencia: { codigo: string; factor: string } | null,
  ) => {
    const clave = `n${b.secuencia}`;
    despachar({
      tipo: 'agregar',
      linea: {
        productoId: producto.id,
        sku: producto.sku,
        nombre: producto.nombre,
        presentacionId: base.id,
        cantidad: equivalencia ? String(Number(equivalencia.factor)) : '1',
        precioManual: null,
        descuento: null,
        equivalencia: equivalencia
          ? { codigo: equivalencia.codigo, cantidad: '1', factor: equivalencia.factor }
          : null,
      },
    });
    setTexto('');
    setEnfocar(clave);
  };
  const ingresar = async (t: string) => {
    if (!t.trim()) return setDialogo({ producto: '' });
    const exacto = await resolverCodigo(empresaId, t.trim()).catch(() => null);
    if (exacto) agregar(exacto.producto, exacto.base, null);
    else setDialogo({ producto: t.trim() });
  };

  const guardar = () => {
    if (!cuerpoPrev) return;
    const c = cuerpoCrear(b, empresaId);
    const comun = {
      ...(c.listaPrecioId ? { listaPrecioId: c.listaPrecioId } : {}),
      ...(c.vendedorId ? { vendedorId: c.vendedorId } : {}),
      ...(c.documentoSolicitado ? { documentoSolicitado: c.documentoSolicitado } : {}),
      validaHasta,
      lineas: c.lineas,
      cargos: c.cargos ?? [],
    };
    if (cot)
      accion.mutate(
        {
          ruta: `/ventas/cotizaciones/${cot.cotizacionId}`,
          metodo: 'PATCH',
          cuerpo: { version: cot.version, ...comun, observaciones: b.observaciones.trim() || null },
        },
        { onSuccess: () => alGuardada(cot.cotizacionId, cot.numeroVisible) },
      );
    else
      accion.mutate(
        {
          ruta: '/ventas/cotizaciones',
          cuerpo: {
            empresaId,
            clienteEntidadId: c.clienteEntidadId,
            canal: c.canal,
            ...comun,
            ...(c.observaciones ? { observaciones: c.observaciones } : {}),
          },
        },
        {
          onSuccess: (r) => {
            const x = r as { cotizacionId: string; numeroVisible: string };
            alGuardada(x.cotizacionId, x.numeroVisible);
          },
        },
      );
  };

  return (
    <div className="pantalla documento-compra" data-pantalla="cotizacion-editor">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Ventas › Cotización</span>
        <h2>{cot ? `Editar ${cot.numeroVisible}` : 'Nueva cotización'}</h2>
      </div>
      <div className="barra-filtros">
        <div className="campo">
          <span className="etiqueta">Cliente</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir cliente"
            disabled={!!cot}
            onClick={() => setDialogo('cliente')}
          >
            {b.cliente ? b.cliente.razonSocial : 'Buscar cliente (F2)…'}
          </button>
        </div>
        <label className="campo">
          <span className="etiqueta">Válida hasta</span>
          <input
            type="date"
            aria-label="Válida hasta"
            value={validaHasta}
            onChange={(e) => setValidaHasta(e.target.value)}
          />
        </label>
        <SelectorBodega
          bodegas={bodegas.lista}
          valor={bodega}
          alCambiar={(id) => despachar({ tipo: 'cabecera', campos: { ubicacionId: id } })}
        />
        <span className="apagado">La bodega solo muestra stock: se elige al convertir en NV.</span>
      </div>
      <GrillaLineas
        ref={grilla}
        empresaId={empresaId}
        listaId={b.listaPrecioId || vista?.cabecera.listaPrecioId || null}
        lineas={b.lineas}
        calculadas={vista?.lineas ?? null}
        reservas={null}
        obsoleto={prev.estado !== 'actualizado'}
        soloLectura={false}
        puedeAgregar={!!b.cliente}
        puedePrecioManual={puede('ventas.precio.manual')}
        disponibilidad={stock.data}
        pedido={vista?.disponibilidad ?? null}
        textoAgregar={texto}
        alCambiarTextoAgregar={setTexto}
        alBuscarProducto={(t) => void ingresar(t)}
        alCambiar={(clave, campos) => despachar({ tipo: 'linea', clave, campos })}
        alQuitar={(clave) => despachar({ tipo: 'quitar', clave })}
      />
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Observaciones</span>
          <input
            aria-label="Observaciones"
            value={b.observaciones}
            onChange={(e) =>
              despachar({ tipo: 'cabecera', campos: { observaciones: e.target.value } })
            }
          />
        </label>
        <span className="espaciador" />
        {vista && (
          <span>
            Neto <b>{pesos(vista.totales.neto)}</b> · impuestos{' '}
            {pesos(vista.totales.totalImpuestos)} · total <b>{pesos(vista.totales.bruto)}</b>
            {prev.estado !== 'actualizado' && <span className="apagado"> (calculando…)</span>}
          </span>
        )}
      </div>
      {prev.error ? <PanelError error={prev.error} /> : null}
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        {alCancelar && (
          <button type="button" onClick={alCancelar}>
            Volver sin guardar
          </button>
        )}
        <button
          type="button"
          className="primario"
          disabled={!cuerpoPrev || accion.isPending || !validaHasta}
          onClick={guardar}
        >
          {cot ? 'Guardar cambios' : 'Guardar cotización'}
        </button>
      </div>
      {dialogo === 'cliente' && (
        <BuscadorCliente
          empresaId={empresaId}
          alElegir={(c) => {
            despachar({ tipo: 'cliente', cliente: clienteDesdeBusqueda(c) });
            setDialogo(null);
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo && typeof dialogo === 'object' && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={b.listaPrecioId || vista?.cabecera.listaPrecioId || null}
          ubicacionId={bodega}
          ubicaciones={bodegas.lista}
          textoInicial={dialogo.producto}
          alElegir={(p) => {
            setDialogo(null);
            agregar(
              p.producto,
              p.base,
              p.presentacion.id !== p.base.id
                ? { codigo: p.presentacion.codigo, factor: p.presentacion.factor_a_base }
                : null,
            );
          }}
          alCerrar={() => setDialogo(null)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha

function FichaCotizacion({
  c,
  alEditar,
  alAbrirNota,
}: {
  c: CotizacionLeida;
  alEditar: () => void;
  alAbrirNota: (notaId: string, numero: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCotizacion(empresaId);
  const [dialogo, setDialogo] = useState<null | 'anular' | 'descartar' | 'convertir'>(null);
  const [bodega, setBodega] = useState('');
  const [imprimir, setImprimir] = useState(false);
  const k = c.contenido.cabecera;
  const ruta = `/ventas/cotizaciones/${c.cotizacionId}`;
  const convertir = () =>
    accion.mutate(
      {
        ruta: `${ruta}/convertir`,
        cuerpo: { version: c.version, ubicacionId: bodega || bodegas.lista[0]?.id },
      },
      {
        onSuccess: (r) => {
          setDialogo(null);
          const n = (r as { nota?: { notaId: string; numeroVisible: string } }).nota;
          if (n) alAbrirNota(n.notaId, n.numeroVisible);
        },
      },
    );
  return (
    <div className="pantalla documento-compra" data-pantalla="cotizacion-ficha">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Ventas › Cotización</span>
        <h2>
          {c.numeroVisible}{' '}
          <span className="insignia tono-neutro" data-estado={c.estado}>
            {NOMBRE_ESTADO_COT[c.estado]}
          </span>
          {!c.vigente && c.estado === 'EMITIDA' && (
            <span className="insignia tono-malo">Vencida</span>
          )}
        </h2>
      </div>
      <dl className="cabecera-od">
        <div>
          <dt>Cliente</dt>
          <dd>{k.clienteRazonSocial}</dd>
        </div>
        <div>
          <dt>RUT</dt>
          <dd className="mono">{k.clienteRut ?? '—'}</dd>
        </div>
        <div>
          <dt>Fecha</dt>
          <dd>{fecha(k.fechaNegocio)}</dd>
        </div>
        <div>
          <dt>Válida hasta</dt>
          <dd>{fecha(c.validaHasta)}</dd>
        </div>
        <div>
          <dt>Documento</dt>
          <dd>{k.documentoSolicitado.toLowerCase()}</dd>
        </div>
        <div>
          <dt>Neto</dt>
          <dd>{pesos(c.contenido.totales.neto)}</dd>
        </div>
        <div>
          <dt>Impuestos</dt>
          <dd>{pesos(c.contenido.totales.totalImpuestos)}</dd>
        </div>
        <div>
          <dt>Total</dt>
          <dd>
            <b>{pesos(c.contenido.totales.bruto)}</b>
          </dd>
        </div>
      </dl>
      <TablaLineasVenta lineas={c.contenido.lineas} />
      {k.observaciones && <p className="apagado">{k.observaciones}</p>}
      {accion.isError && !dialogo && <PanelError error={accion.error} />}
      <div className="acciones-od">
        {c.estado === 'BORRADOR' && puede('ventas.cotizacion.editar') && (
          <button type="button" onClick={alEditar}>
            Editar
          </button>
        )}
        {c.estado === 'BORRADOR' && puede('ventas.cotizacion.emitir') && (
          <button
            type="button"
            className="primario"
            disabled={accion.isPending}
            onClick={() =>
              accion.mutate({ ruta: `${ruta}/emitir`, cuerpo: { version: c.version } })
            }
          >
            Emitir
          </button>
        )}
        {c.estado === 'BORRADOR' && (
          <button type="button" onClick={() => setDialogo('descartar')}>
            Descartar
          </button>
        )}
        {c.estado === 'EMITIDA' && puede('ventas.cotizacion.convertir') && (
          <button type="button" className="primario" onClick={() => setDialogo('convertir')}>
            Convertir en nota de venta
          </button>
        )}
        {c.estado === 'EMITIDA' && puede('ventas.cotizacion.anular') && (
          <button type="button" onClick={() => setDialogo('anular')}>
            Anular
          </button>
        )}
        {c.notaVentaId && (
          <button type="button" onClick={() => alAbrirNota(c.notaVentaId!, 'Nota')}>
            Abrir la nota de venta
          </button>
        )}
        <button type="button" onClick={() => setImprimir(true)}>
          Imprimir cotización
        </button>
      </div>
      {(dialogo === 'anular' || dialogo === 'descartar') && (
        <DialogoMotivo
          titulo={`${dialogo === 'anular' ? 'Anular' : 'Descartar'} ${c.numeroVisible}`}
          textoBoton={dialogo === 'anular' ? 'Anular' : 'Descartar'}
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              { ruta: `${ruta}/${dialogo}`, cuerpo: { version: c.version, motivo } },
              { onSuccess: () => setDialogo(null) },
            )
          }
          alCerrar={() => setDialogo(null)}
        />
      )}
      {dialogo === 'convertir' && (
        <div className="panel-revision" role="dialog" aria-label="Convertir en nota de venta">
          <p>
            Se crea una nota de venta con estos productos, recalculada a los precios de hoy. Elige
            la bodega desde donde se venderá.
          </p>
          <SelectorBodega
            bodegas={bodegas.lista}
            valor={bodega || bodegas.lista[0]?.id || ''}
            alCambiar={setBodega}
          />
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setDialogo(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={accion.isPending}
              onClick={convertir}
            >
              Crear nota de venta
            </button>
          </div>
        </div>
      )}
      {imprimir && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <article className="impreso">
            <EncabezadoImpreso
              empresa={empresa!.razon_social}
              titulo="Cotización"
              numero={c.numeroVisible}
              detalle={
                <dl className="impreso-datos">
                  <dt>Cliente</dt>
                  <dd>
                    {k.clienteRazonSocial} {k.clienteRut ? `· ${k.clienteRut}` : ''}
                  </dd>
                  <dt>Fecha</dt>
                  <dd>{fecha(k.fechaNegocio)}</dd>
                  <dt>Válida hasta</dt>
                  <dd>{fecha(c.validaHasta)}</dd>
                </dl>
              }
            />
            <TablaLineasVenta lineas={c.contenido.lineas} impresa />
            <p className="der">
              Neto {pesos(c.contenido.totales.neto)} · Impuestos{' '}
              {pesos(c.contenido.totales.totalImpuestos)} ·{' '}
              <b>Total {pesos(c.contenido.totales.bruto)}</b>
            </p>
            <p className="apagado">Precios sujetos a stock disponible a la fecha de la compra.</p>
          </article>
        </Imprimir>
      )}
    </div>
  );
}

/** Líneas ya calculadas por el backend (cotización, impresos de la NV). */
export function TablaLineasVenta({
  lineas,
  impresa = false,
}: {
  lineas: LineaNota[];
  impresa?: boolean;
}) {
  return (
    <table className={impresa ? 'impreso-tabla' : 'grilla compacta'} aria-label="Líneas">
      <thead>
        <tr>
          <th>N°</th>
          <th>Código</th>
          <th>Descripción</th>
          <th className="der">Cantidad</th>
          <th className="der">Precio</th>
          <th className="der">Desc.</th>
          <th className="der">Total</th>
        </tr>
      </thead>
      <tbody>
        {lineas.map((l) => (
          <tr key={l.numero}>
            <td>{l.numero}</td>
            <td className="mono">{l.sku}</td>
            <td>{l.descripcion}</td>
            <td className="der">
              {decimal(l.cantidad)} {l.presentacionCodigo}
            </td>
            <td className="der">{pesos(l.precioAplicado)}</td>
            <td className="der">{Number(l.descuento) ? pesos(l.descuento) : ''}</td>
            <td className="der">{pesos(l.bruto)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ================================================================ consulta

export function Cotizaciones({
  alAbrir,
  alNueva,
}: {
  alAbrir: (id: string, numero: string) => void;
  alNueva: () => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState<EstadoCotizacion | ''>('');
  const [pagina, setPagina] = useState(1);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'cotizaciones', 'lista', estado, pagina],
    queryFn: () =>
      api.get<{ items: CotizacionListada[] }>('/ventas/cotizaciones', {
        query: { empresaId, estado: estado || undefined, pagina, tamano: 50 },
      }),
  });
  const columnas: Columna<CotizacionListada>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (c) => <span className="mono">{c.numeroVisible}</span>,
      ancho: '8rem',
    },
    { id: 'f', titulo: 'Fecha', celda: (c) => fecha(c.fecha_negocio), ancho: '7rem' },
    { id: 'c', titulo: 'Cliente', celda: (c) => c.cliente_razon_social },
    { id: 'v', titulo: 'Válida hasta', celda: (c) => fecha(c.valida_hasta), ancho: '8rem' },
    { id: 'e', titulo: 'Estado', celda: (c) => NOMBRE_ESTADO_COT[c.estado], ancho: '9rem' },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="cotizaciones">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado de la cotización"
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value as EstadoCotizacion | '');
              setPagina(1);
            }}
          >
            <option value="">Todas</option>
            {(Object.keys(NOMBRE_ESTADO_COT) as EstadoCotizacion[]).map((k) => (
              <option key={k} value={k}>
                {NOMBRE_ESTADO_COT[k]}
              </option>
            ))}
          </select>
        </label>
        <span className="espaciador" />
        {puede('ventas.cotizacion.crear') && (
          <button type="button" className="primario" onClick={alNueva}>
            Nueva cotización
          </button>
        )}
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Cotizaciones"
        columnas={columnas}
        filas={q.data?.items ?? []}
        clave={(c) => c.id}
        cargando={q.isLoading}
        vacio="Sin cotizaciones"
        alAbrir={(c) => alAbrir(c.id, c.numeroVisible)}
        paginacion={{ pagina, hayMas: (q.data?.items.length ?? 0) >= 50, alCambiar: setPagina }}
        autofoco
      />
    </div>
  );
}
